/**
 * Browser-safe recovery-code shapes for pinned GoTrue v2.197.0 (#2765).
 *
 * GoTrue owns the hashes, one-time consumption, lockout and factor audit. The
 * only plaintext this package ever holds is the one response to generate or
 * regenerate. It lives behind a reveal/clear envelope and is deliberately not
 * a serialisable property of that envelope.
 */

export interface RecoveryCodeStatus {
  factorId: string
  total: number
  remaining: number
}

export interface RecoveryCodeMaterial {
  /** A fresh copy. Callers must not retain it after the reveal frame closes. */
  codes: readonly string[]
}

export interface RecoveryCodeReveal {
  factorId: string
  total: number
  /** Returns a fresh copy until clear() is called, then null. */
  reveal(): RecoveryCodeMaterial | null
  /** Drops the package's final reference to every plaintext code. */
  clear(): void
}

type RawRecoveryCodes = {
  id?: unknown
  type?: unknown
  total?: unknown
  remaining?: unknown
  codes?: unknown
}

const FACTOR_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const CODE = /^[a-z2-7]{13,32}$/

const boundedCount = (value: unknown): value is number =>
  Number.isInteger(value) && Number(value) >= 0 && Number(value) <= 16

/** Status deliberately projects only caller-owned counts, never lockout state. */
export function readRecoveryCodeStatus(value: unknown): RecoveryCodeStatus | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as RawRecoveryCodes
  if (typeof raw.id !== 'string' || !FACTOR_ID.test(raw.id) || raw.type !== 'recovery_code') return null
  if (!boundedCount(raw.total) || raw.total < 4 || !boundedCount(raw.remaining) || raw.remaining > raw.total) return null
  if (raw.codes !== undefined) return null
  return Object.freeze({ factorId: raw.id, total: raw.total, remaining: raw.remaining })
}

/** Parse the one response in which GoTrue returns plaintext recovery codes. */
export function readRecoveryCodeReveal(value: unknown): RecoveryCodeReveal | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as RawRecoveryCodes
  if (typeof raw.id !== 'string' || !FACTOR_ID.test(raw.id) || raw.type !== 'recovery_code') return null
  if (!boundedCount(raw.total) || raw.total < 4 || raw.remaining !== undefined) return null
  if (!Array.isArray(raw.codes) || raw.codes.length !== raw.total) return null
  if (raw.codes.some((code) => typeof code !== 'string' || !CODE.test(code))) return null
  if (new Set(raw.codes).size !== raw.codes.length) return null

  let material: string[] | null = [...raw.codes]
  const envelope: RecoveryCodeReveal = {
    factorId: raw.id,
    total: raw.total,
    reveal: () => material ? { codes: [...material] } : null,
    clear: () => {
      material?.fill('')
      material = null
    },
  }
  return Object.freeze(envelope)
}

/** Match GoTrue's canonicalisation while bounding work before the request. */
export function normalizeRecoveryCode(value: string): string {
  if (value.length > 128) return ''
  const normalized = value.replace(/[\s-]/gu, '').toLowerCase()
  return CODE.test(normalized) ? normalized : ''
}
